import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

function parseYyyyMmDdToLocalDate(s: string): Date {
  if (!s || s.length < 10) return new Date();
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m, d);
}

export function formatIsoForDayLabel(isoKey: string): string {
  if (!isoKey || isoKey === "_unknown" || isoKey.startsWith("_")) return "—";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoKey)) return isoKey;
  const d = parseYyyyMmDdToLocalDate(isoKey);
  if (Number.isNaN(d.getTime())) return isoKey;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function compareSeriesLegendEntries(a: any, b: any): number {
  const ka = String(a?.dataKey ?? a?.value ?? "");
  const kb = String(b?.dataKey ?? b?.value ?? "");
  if (/^\d{4}-\d{2}-\d{2}$/.test(ka) && /^\d{4}-\d{2}-\d{2}$/.test(kb)) {
    return ka.localeCompare(kb);
  }
  return ka.localeCompare(kb);
}

function formatCount(n: unknown): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return Math.round(v).toLocaleString("en-GB");
}

export type HourlyTooltipEntity = "email" | "ticket";

function tooltipHourHeading(
  label: unknown,
  entity: HourlyTooltipEntity,
): { title: string; subtitle: string } {
  const noun = entity === "ticket" ? "Tickets" : "Emails";
  if (label == null || label === "") {
    return { title: `${noun} volume`, subtitle: "By selected day" };
  }
  const s = String(label).trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) {
    return { title: s, subtitle: `${noun} this hour, by day` };
  }
  return { title: s, subtitle: "By selected day" };
}

export type HourlyTooltipConfig = {
  selDates: string[];
  exS: string;
  exE: string;
  chartRootRef?: React.RefObject<HTMLElement | null>;
  dateColors?: Record<string, string>;
};

export type HourlyDaySeriesTooltipProps = {
  active?: boolean;
  payload?: any[];
  label?: unknown;
  coordinate?: { x?: number; y?: number };
  configRef: React.MutableRefObject<HourlyTooltipConfig>;
  teamId?: string | null;
  hoveredPointRef: React.MutableRefObject<any>;
  entity?: HourlyTooltipEntity;
};

const FALLBACK_SERIES_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
];

type TooltipRow = {
  dataKey: string;
  name: string;
  value: number;
  color: string;
};

type StickyTooltipState = {
  rows: TooltipRow[];
  hourTitle: string;
  hourSubtitle: string;
  totalCount: number;
  showShare: boolean;
  left: number;
  top: number;
  teamId?: string | null;
  exS: string;
  exE: string;
};

function seriesKeyFromPayload(p: any): string {
  return String(p?.dataKey ?? p?.name ?? "");
}

function sortIsoDateKeys(dates: string[]): string[] {
  return [...dates].sort((a, b) => a.localeCompare(b));
}

function buildTooltipRows(
  selDates: string[],
  hoveredPoint: Record<string, unknown> | undefined,
  payload: any[],
  dateColors?: Record<string, string>,
): TooltipRow[] {
  const payloadByKey = new Map<string, any>();
  payload.forEach((p) => {
    const key = seriesKeyFromPayload(p);
    if (key) payloadByKey.set(key, p);
  });

  return sortIsoDateKeys(selDates)
    .map((dateKey, index) => {
      const fromPayload = payloadByKey.get(dateKey);
      const rawValue = hoveredPoint?.[dateKey] ?? fromPayload?.value ?? 0;
      return {
        dataKey: dateKey,
        name: formatIsoForDayLabel(dateKey),
        value: Number(rawValue) || 0,
        color:
          dateColors?.[dateKey] ??
          fromPayload?.color ??
          fromPayload?.fill ??
          FALLBACK_SERIES_COLORS[index % FALLBACK_SERIES_COLORS.length],
      };
    })
    .sort((a, b) => {
      if (b.value !== a.value) return b.value - a.value;
      return a.dataKey.localeCompare(b.dataKey);
    });
}

function portalPosition(
  coordinate: { x?: number; y?: number },
  chartRoot: HTMLElement,
): { left: number; top: number } {
  const rect = chartRoot.getBoundingClientRect();
  const left = Math.min(
    Math.max(12, rect.left + (coordinate.x ?? 0) + 16),
    window.innerWidth - 420,
  );

  const tooltipHeight = Math.min(460, window.innerHeight * 0.72);
  const anchorY = rect.top + (coordinate.y ?? 0);
  const spaceBelow = window.innerHeight - anchorY - 20;
  const spaceAbove = anchorY - 20;

  let top =
    spaceBelow >= tooltipHeight || spaceBelow >= spaceAbove
      ? anchorY + 12
      : anchorY - tooltipHeight - 12;

  top = Math.max(12, Math.min(top, window.innerHeight - tooltipHeight - 12));
  return { left, top };
}

const scrollListStyle: React.CSSProperties = {
  flex: "1 1 auto",
  minHeight: "120px",
  maxHeight: "min(300px, 42vh)",
  overflowY: "scroll",
  overscrollBehavior: "contain",
  paddingRight: "6px",
  marginRight: "-2px",
  scrollbarWidth: "thin",
  scrollbarColor: "#94a3b8 #e2e8f0",
  borderTop: "1px solid rgba(226, 232, 240, 0.6)",
  borderBottom: "1px solid rgba(226, 232, 240, 0.6)",
  paddingTop: "6px",
  paddingBottom: "6px",
};

function TooltipBody({
  state,
  entity,
}: {
  state: StickyTooltipState;
  entity: HourlyTooltipEntity;
}) {
  const { rows, hourTitle, hourSubtitle, totalCount, showShare, teamId, exS, exE } =
    state;

  return (
    <div
      role="status"
      style={{
        pointerEvents: "auto",
        display: "flex",
        flexDirection: "column",
        maxHeight: "min(460px, 72vh)",
        background:
          "linear-gradient(165deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.98) 55%, rgba(241,245,249,0.95) 100%)",
        border: "1px solid rgba(148, 163, 184, 0.45)",
        borderRadius: "14px",
        padding: "14px 16px",
        boxShadow:
          "0 8px 24px -4px rgba(15, 23, 42, 0.18), 0 16px 40px -8px rgba(15, 23, 42, 0.12)",
        minWidth: "260px",
        maxWidth: "min(400px, 92vw)",
        cursor: teamId && exS && exE ? "pointer" : "default",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
          marginBottom: "8px",
          paddingBottom: "10px",
          borderBottom: "1px solid rgba(226, 232, 240, 0.95)",
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
            border: "1px solid #c7d2fe",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#4f46e5"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 800,
              fontSize: "17px",
              color: "#0f172a",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {hourTitle}
          </div>
          <div
            style={{
              marginTop: "3px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#64748b",
            }}
          >
            {hourSubtitle}
          </div>
          {rows.length > 1 && (
            <div
              style={{
                marginTop: "4px",
                fontSize: "11px",
                fontWeight: 600,
                color: "#6366f1",
              }}
            >
              {rows.length} days — scroll inside box ↕
            </div>
          )}
        </div>
      </div>

      <div
        className="hourly-tooltip-scroll"
        style={scrollListStyle}
        onWheel={(e) => e.stopPropagation()}
      >
        {rows.map((entry, index) => {
          const share =
            showShare && totalCount > 0
              ? Math.round((entry.value / totalCount) * 100)
              : null;
          const hasValue = entry.value > 0;
          return (
            <div
              key={entry.dataKey || index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "14px",
                marginBottom: "4px",
                padding: "7px 10px",
                borderRadius: "8px",
                backgroundColor: hasValue ? `${entry.color}18` : `${entry.color}08`,
                border: `1px solid ${hasValue ? `${entry.color}40` : `${entry.color}20`}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "3px",
                    flexShrink: 0,
                    backgroundColor: entry.color,
                  }}
                />
                <span
                  style={{
                    fontSize: "12px",
                    color: hasValue ? "#1e293b" : "#64748b",
                    fontWeight: hasValue ? 700 : 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {entry.name}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "6px",
                  flexShrink: 0,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                <span
                  style={{
                    fontSize: "15px",
                    fontWeight: 800,
                    color: hasValue ? "#0f172a" : "#94a3b8",
                  }}
                >
                  {formatCount(entry.value)}
                </span>
                {share != null && share > 0 && (
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#94a3b8",
                    }}
                  >
                    {share}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {rows.length > 1 && (
        <div
          style={{
            flexShrink: 0,
            paddingTop: "10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <span
            style={{
              fontSize: "12px",
              color: "#64748b",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Total this hour
          </span>
          <span
            style={{
              fontSize: "16px",
              fontWeight: 800,
              color: "#4f46e5",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatCount(totalCount)}
          </span>
        </div>
      )}
      {teamId && exS && exE && (
        <p
          style={{
            flexShrink: 0,
            margin: "8px 0 0 0",
            fontSize: "11px",
            color: "#64748b",
            fontWeight: 600,
          }}
        >
          Click the chart to open details for this hour
        </p>
      )}
    </div>
  );
}

export function HourlyDaySeriesTooltip({
  active,
  payload = [],
  label,
  coordinate,
  configRef,
  teamId,
  hoveredPointRef,
  entity = "email",
}: HourlyDaySeriesTooltipProps) {
  const { selDates, exS, exE, chartRootRef, dateColors } = configRef.current;
  const [sticky, setSticky] = useState<StickyTooltipState | null>(null);
  const [hoveringPortal, setHoveringPortal] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!active || !Array.isArray(payload) || !payload.length) {
      if (!hoveringPortal) {
        clearHideTimer();
        hideTimerRef.current = setTimeout(() => setSticky(null), 120);
      }
      return;
    }

    clearHideTimer();

    const hoveredPoint = payload.find((p) => p?.payload)?.payload as
      | Record<string, unknown>
      | undefined;

    const rows = buildTooltipRows(selDates, hoveredPoint, payload, dateColors);
    if (!rows.length) {
      setSticky(null);
      return;
    }

    if (hoveredPoint) {
      hoveredPointRef.current = hoveredPoint;
    }

    const totalCount = rows.reduce((sum, p) => sum + p.value, 0);
    const { title: hourTitle, subtitle: hourSubtitle } = tooltipHourHeading(
      label,
      entity,
    );
    const showShare = totalCount > 0 && rows.length > 1;

    const chartRoot = chartRootRef?.current;
    setSticky((prev) => {
      const pos =
        coordinate && chartRoot
          ? portalPosition(coordinate, chartRoot)
          : prev
            ? { left: prev.left, top: prev.top }
            : { left: 24, top: 24 };

      return {
        rows,
        hourTitle,
        hourSubtitle,
        totalCount,
        showShare,
        left: pos.left,
        top: pos.top,
        teamId,
        exS,
        exE,
      };
    });
  }, [
    active,
    payload,
    label,
    coordinate,
    selDates,
    dateColors,
    teamId,
    exS,
    exE,
    chartRootRef,
    entity,
    hoveredPointRef,
    hoveringPortal,
  ]);

  useEffect(() => () => clearHideTimer(), []);

  if (!sticky || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <>
      <style>{`
        .hourly-tooltip-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .hourly-tooltip-scroll::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 4px;
        }
        .hourly-tooltip-scroll::-webkit-scrollbar-thumb {
          background: #94a3b8;
          border-radius: 4px;
        }
        .hourly-tooltip-scroll::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
      `}</style>
      <div
        style={{
          position: "fixed",
          left: sticky.left,
          top: sticky.top,
          zIndex: 100000,
          pointerEvents: "auto",
        }}
        onMouseEnter={() => {
          clearHideTimer();
          setHoveringPortal(true);
        }}
        onMouseLeave={() => {
          setHoveringPortal(false);
          hideTimerRef.current = setTimeout(() => setSticky(null), 120);
        }}
      >
        <TooltipBody state={sticky} entity={entity} />
      </div>
    </>,
    document.body,
  );
}

export const hourlyChartTooltipProps = {
  allowEscapeViewBox: { x: true as const, y: true as const },
  isAnimationActive: false,
  wrapperStyle: {
    zIndex: 100000,
    outline: "none" as const,
    pointerEvents: "none" as const,
    overflow: "visible" as const,
  },
};

export const hourlyChartLegendProps = {
  wrapperStyle: {
    zIndex: 1,
    pointerEvents: "auto" as const,
  },
};
